import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, PersonOff, SportsTennis, TrendingUp, EmojiEvents } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { getAvatarUrl } from '../utils/avatar';

export type PlayerProfileTarget =
  | {
      kind: 'registered';
      userId: number;
      name?: string;
      avatarUrl?: string | null;
      prefetchedProfile?: PublicUserProfile | null;
    }
  | { kind: 'guest'; name: string };

interface PublicUserProfile {
  id: number;
  name: string;
  avatar_url?: string | null;
  initial_elo?: number | null;
  elo_rating?: number | null;
  total_matches: number;
  total_wins: number;
  win_rate: number;
  preferred_side?: string | null;
  club_name?: string | null;
}

interface PlayerProfileModalProps {
  open: boolean;
  target: PlayerProfileTarget | null;
  onClose: () => void;
}

function StatBlock({ label, value }: { label: string; value: string | number }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', px: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
        {label}
      </Typography>
    </Box>
  );
}

export const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({ open, target, onClose }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !target) {
      setProfile(null);
      setError('');
      return;
    }
    if (target.kind === 'guest') {
      setProfile(null);
      setError('');
      setLoading(false);
      return;
    }
    if (target.prefetchedProfile) {
      setProfile(target.prefetchedProfile);
      setError('');
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(!target.prefetchedProfile);
        setError('');
        const res = await apiService.getPublicUserProfile(target.userId);
        if (!cancelled) {
          setProfile(res.data);
          setError('');
        }
      } catch {
        if (!cancelled) {
          if (target.prefetchedProfile) {
            setProfile(target.prefetchedProfile);
            setError('');
          } else {
            setError(t('tournamentPublic.playerProfile.loadError', 'Failed to load profile'));
            setProfile(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, target, t]);

  const displayName =
    target?.kind === 'guest'
      ? target.name
      : profile?.name || target?.name || t('tournamentPublic.unknown');

  const avatarSrc =
    target?.kind === 'registered'
      ? getAvatarUrl(profile?.avatar_url ?? target.avatarUrl)
      : '';

  const showInitial = !avatarSrc;

  const preferredSideLabel = (side?: string | null) => {
    if (!side) return '—';
    const key = `tournamentPublic.playerProfile.side.${side.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : side;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#141414',
          backgroundImage: 'none',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 3,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.7)', zIndex: 1 }}
        aria-label={t('common.close', 'Close')}
      >
        <Close />
      </IconButton>
      <DialogContent sx={{ pt: 3, pb: 3, color: 'white' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={avatarSrc || undefined}
            sx={{
              width: 72,
              height: 72,
              fontSize: 28,
              border: '2px solid rgba(255,255,255,0.15)',
              bgcolor: 'primary.main',
            }}
          >
            {showInitial ? displayName.charAt(0).toUpperCase() : null}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
            {displayName}
          </Typography>

          {target?.kind === 'guest' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                width: '100%',
              }}
            >
              <PersonOff sx={{ color: 'rgba(255,255,255,0.5)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                {t('tournamentPublic.playerProfile.notRegisteredYet', 'Not registered yet')}
              </Typography>
            </Box>
          )}

          {target?.kind === 'registered' && loading && (
            <CircularProgress size={28} sx={{ color: 'primary.main', my: 2 }} />
          )}

          {target?.kind === 'registered' && error && !loading && (
            <Typography variant="body2" sx={{ color: '#f87171', textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          {target?.kind === 'registered' && profile && !loading && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  mt: 1,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <StatBlock
                  label={t('tournamentPublic.playerProfile.matches', 'Matches')}
                  value={profile.total_matches}
                />
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                <StatBlock
                  label={t('tournamentPublic.playerProfile.wins', 'Wins')}
                  value={profile.total_wins}
                />
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                <StatBlock
                  label={t('tournamentPublic.playerProfile.winRate', 'Win rate')}
                  value={`${(profile.win_rate || 0).toFixed(1)}%`}
                />
              </Box>

              <Box sx={{ width: '100%', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                  <TrendingUp sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t('tournamentPublic.playerProfile.elo', 'ELO')}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {(profile.elo_rating ?? profile.initial_elo ?? 0).toFixed(1)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                  <SportsTennis sx={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t('tournamentPublic.playerProfile.preferredSide', 'Preferred side')}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {preferredSideLabel(profile.preferred_side)}
                  </Typography>
                </Box>
                {profile.club_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                    <EmojiEvents sx={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {t('tournamentPublic.playerProfile.club', 'Club')}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                      {profile.club_name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export function playerSlotTarget(
  userId: number | null | undefined,
  name: string | null | undefined,
  avatarUrl?: string | null,
  _pendingInvite?: boolean
): PlayerProfileTarget | null {
  if (!name?.trim()) return null;
  if (userId) {
    return { kind: 'registered', userId, name: name.trim(), avatarUrl };
  }
  return { kind: 'guest', name: name.trim() };
}
